/* eslint-disable max-lines */
import {
  CrossingDirectionFilter,
  CrossingMethod,
  PresenceEntityType,
  PresenceLiveness,
  PresenceRule,
  PresenceRuleType,
  PresenceZoneKind,
} from '@hakimo-ui/hakimo/types';

/** The server's name for the implicit whole-camera zone */
export const WHOLE_CAMERA_ZONE = '__frame__';
export const WHOLE_CAMERA_LABEL = 'Whole camera';

/** The worker evaluates over a 60-minute window; the API refuses more. */
export const MAX_RULE_WINDOW_MINUTES = 60;

export interface DirectionOption {
  id: CrossingDirectionFilter;
  label: string;
}

/**
 * Named by the arrow drawn on the line rather than "left"/"right", which
 * depend on which way you imagine walking it. The arrow points to the side
 * an "ltr" crossing lands on.
 */
export const DIRECTION_OPTIONS: DirectionOption[] = [
  { id: 'any', label: 'Either way' },
  { id: 'ltr', label: "In the arrow's direction" },
  { id: 'rtl', label: 'Against the arrow' },
];

export interface MethodOption {
  id: CrossingMethod;
  label: string;
  hint: string;
}

export const METHOD_OPTIONS: MethodOption[] = [
  {
    id: 'observed',
    label: 'Seen crossing',
    hint: 'The camera watched the person go through the line.',
  },
  {
    id: 'appeared',
    label: 'Appears at the line',
    hint: 'A person first seen right at the line is read as having come through it — for a door whose far side is out of view.',
  },
  {
    id: 'vanished',
    label: 'Disappears at the line',
    hint: 'A person last seen right at the line is read as having gone through it.',
  },
];

export interface EntityOption {
  id: PresenceEntityType;
  label: string;
}

export const ENTITY_OPTIONS: EntityOption[] = [
  { id: 'person', label: 'People' },
  { id: 'vehicle', label: 'Vehicles' },
];

export interface RuleTypeOption {
  id: PresenceRuleType;
  label: string;
  hint: string;
}

/**
 * Everything the form needs to know about a rule type. A field is shown
 * when its entry is present; the number is its default.
 */
interface RuleSpec {
  label: string;
  hint: string;
  zoneKind: PresenceZoneKind;
  count?: { label: string; default: number };
  /** "For at least this many minutes" */
  minutes?: number;
  /** A trailing window, in minutes */
  window?: number;
}

const RULE_SPECS: Record<PresenceRuleType, RuleSpec> = {
  dwell: {
    label: 'Someone stays',
    hint: 'A person is in the zone for longer than allowed.',
    zoneKind: 'area',
    minutes: 5,
  },
  absence: {
    label: 'Nobody present',
    hint: 'The zone is empty for longer than allowed.',
    zoneKind: 'area',
    minutes: 10,
  },
  max_occupancy: {
    label: 'Too many people',
    hint: 'The zone holds at least this many people.',
    zoneKind: 'area',
    count: { label: 'At least this many people', default: 10 },
    minutes: 0,
  },
  min_occupancy: {
    label: 'Too few people',
    hint: 'The zone holds fewer than this many people for too long.',
    zoneKind: 'area',
    count: { label: 'Fewer than this many people', default: 2 },
    minutes: 10,
  },
  max_visitors: {
    label: 'Too many visitors',
    hint: 'More different people pass through than allowed in a window.',
    zoneKind: 'area',
    count: { label: 'More than this many people', default: 30 },
    window: 60,
  },
  line_crossing: {
    label: 'Someone crosses a line',
    hint: 'A person or vehicle crosses a line you drew, in the direction you choose.',
    zoneKind: 'line',
    count: {
      label: 'At least this many crossings (1 alerts on each)',
      default: 1,
    },
    window: 10,
  },
};

export const RULE_TYPE_OPTIONS: RuleTypeOption[] = (
  Object.keys(RULE_SPECS) as PresenceRuleType[]
).map((id) => ({ id, label: RULE_SPECS[id].label, hint: RULE_SPECS[id].hint }));

/** The kind of zone a rule type addresses. */
export function zoneKindFor(type: PresenceRuleType): PresenceZoneKind {
  return RULE_SPECS[type].zoneKind;
}

export function isLineRule(type: PresenceRuleType): boolean {
  return zoneKindFor(type) === 'line';
}

/** The count field's label, or null when the rule has no count. */
export function countLabel(type: PresenceRuleType): string | null {
  return RULE_SPECS[type].count?.label ?? null;
}

export function usesCount(type: PresenceRuleType): boolean {
  return RULE_SPECS[type].count !== undefined;
}

export function usesMinutes(type: PresenceRuleType): boolean {
  return RULE_SPECS[type].minutes !== undefined;
}

/** Rule types that count over a trailing window. */
export function isWindowedType(type: PresenceRuleType): boolean {
  return RULE_SPECS[type].window !== undefined;
}

/**
 * Whether the rule's window means anything. A crossing rule that alerts on
 * every crossing (a count of 1) has nothing to count over: the worker
 * reports any crossing it can still see, however late the clip arrived.
 */
export function usesWindow(
  rule: Pick<PresenceRule, 'type' | 'count'>
): boolean {
  return (
    isWindowedType(rule.type) &&
    !(rule.type === 'line_crossing' && rule.count <= 1)
  );
}

function isValidWindow(value: number): boolean {
  return value > 0 && value <= MAX_RULE_WINDOW_MINUTES;
}

/**
 * The rule as it is sent. The API checks the window of every windowed rule,
 * including a crossing rule whose window the form hides, so a hidden window
 * left out of range is put back to the default rather than refused unseen.
 */
export function ruleForSave(rule: PresenceRule): PresenceRule {
  const hiddenAndInvalid =
    isWindowedType(rule.type) &&
    !usesWindow(rule) &&
    !isValidWindow(rule.windowMinutes);
  return hiddenAndInvalid
    ? { ...rule, windowMinutes: defaultWindow(rule.type) }
    : rule;
}

function defaultWindow(type: PresenceRuleType): number {
  return RULE_SPECS[type].window ?? MAX_RULE_WINDOW_MINUTES;
}

function defaultCount(type: PresenceRuleType): number {
  return RULE_SPECS[type].count?.default ?? 1;
}

function defaultMinutes(type: PresenceRuleType): number {
  return RULE_SPECS[type].minutes ?? 0;
}

/** Rules that read silence as evidence, and so depend on clip cadence. */
export function isLivenessGated(type: PresenceRuleType): boolean {
  return type === 'absence' || type === 'min_occupancy';
}

export function defaultRule(
  type: PresenceRuleType,
  zone: { id: string | null; name: string },
  cameraId: string
): PresenceRule {
  return {
    type,
    zoneId: zone.id,
    zoneName: zone.name,
    cameraId,
    enabled: true,
    count: defaultCount(type),
    thresholdMinutes: defaultMinutes(type),
    windowMinutes: defaultWindow(type),
    minPresenceRatio: 0.1,
    livenessGapMinutes: 5,
    direction: 'any',
    entityTypes: ['person'],
    methods: ['observed'],
  };
}

/** Switch an existing rule's type, keeping what still applies. */
export function withType(
  rule: PresenceRule,
  type: PresenceRuleType
): PresenceRule {
  if (rule.type === type) return rule;
  // A count of people and a count of crossings are different units.
  const sameUnit = zoneKindFor(rule.type) === zoneKindFor(type);
  return {
    ...rule,
    type,
    count: usesCount(type) ? (sameUnit && rule.count) || defaultCount(type) : 1,
    thresholdMinutes: defaultMinutes(type),
    windowMinutes:
      isWindowedType(type) && !isWindowedType(rule.type)
        ? defaultWindow(type)
        : rule.windowMinutes,
  };
}

export function zoneLabel(zoneName: string): string {
  return zoneName === WHOLE_CAMERA_ZONE ? WHOLE_CAMERA_LABEL : zoneName;
}

export function isWholeCamera(zoneName: string): boolean {
  return zoneName === WHOLE_CAMERA_ZONE;
}

/**
 * Does this rule address that zone?
 *
 * The id is the answer whenever the rule carries one -- a rename cannot
 * detach it, and an id this camera does not have is not a match. Only a
 * legacy rule, written before ids, is matched by the name it carries.
 */
export function ruleTargetsZone(
  rule: PresenceRule,
  zone: { id: string; name: string }
): boolean {
  return rule.zoneId ? rule.zoneId === zone.id : rule.zoneName === zone.name;
}

function minutes(n: number): string {
  return `${n} minute${n === 1 ? '' : 's'}`;
}

function people(n: number): string {
  return `${n} ${n === 1 ? 'person' : 'people'}`;
}

function entityPhrase(types: PresenceEntityType[]): string {
  const set = new Set(types);
  if (set.size === 1 && set.has('vehicle')) return 'A vehicle';
  if (set.size === 1 && set.has('person')) return 'A person';
  return 'A person or vehicle';
}

function directionPhrase(direction: CrossingDirectionFilter): string {
  if (direction === 'ltr') return " in the arrow's direction";
  if (direction === 'rtl') return ' against the arrow';
  return '';
}

/**
 * How the crossing may be established, in words. "crosses" is reserved for
 * a crossing the camera watched; the inferences say what they are.
 */
function crossingVerb(methods: CrossingMethod[], zone: string): string {
  const set = new Set(methods.length ? methods : ['observed']);
  const inferred = [
    set.has('appeared') ? 'appears' : null,
    set.has('vanished') ? 'disappears' : null,
  ].filter((v): v is string => v !== null);
  if (!set.has('observed')) return `${inferred.join(' or ')} at ${zone}`;
  return inferred.length
    ? `crosses ${zone} (or ${inferred.join(' or ')} there)`
    : `crosses ${zone}`;
}

function describeCrossing(rule: PresenceRule): string {
  const zone = zoneLabel(rule.zoneName);
  const direction = directionPhrase(rule.direction);
  if (rule.count <= 1) {
    return `${entityPhrase(rule.entityTypes)} ${crossingVerb(
      rule.methods,
      zone
    )}${direction}`;
  }
  const set = new Set(rule.entityTypes);
  const by = set.size === 1 && set.has('vehicle') ? 'vehicle ' : '';
  return `${
    rule.count
  } or more ${by}crossings of ${zone}${direction} in ${minutes(
    rule.windowMinutes
  )}`;
}

/** The rule as the sentence the form builds. */
export function describeRule(rule: PresenceRule): string {
  if (rule.type === 'line_crossing') return describeCrossing(rule);
  if (isWholeCamera(rule.zoneName)) return describeWholeCamera(rule);
  const zone = zoneLabel(rule.zoneName);
  switch (rule.type) {
    case 'dwell':
      return `A person stays in ${zone} for ${minutes(rule.thresholdMinutes)}`;
    case 'absence':
      return `${zone} is empty for ${minutes(rule.thresholdMinutes)}`;
    case 'max_occupancy':
      return rule.thresholdMinutes > 0
        ? `${zone} has ${rule.count} or more people for ${minutes(
            rule.thresholdMinutes
          )}`
        : `${zone} has ${rule.count} or more people`;
    case 'min_occupancy':
      return `${zone} has fewer than ${people(rule.count)} for ${minutes(
        rule.thresholdMinutes
      )}`;
    case 'max_visitors':
      return `More than ${people(rule.count)} pass through ${zone} in ${minutes(
        rule.windowMinutes
      )}`;
  }
}

/** The whole-camera zone is not a place, so the sentence says "in view". */
function describeWholeCamera(rule: PresenceRule): string {
  switch (rule.type) {
    case 'dwell':
      return `A person stays in view for ${minutes(rule.thresholdMinutes)}`;
    case 'absence':
      return `Nobody in view for ${minutes(rule.thresholdMinutes)}`;
    case 'max_occupancy':
      return rule.thresholdMinutes > 0
        ? `${rule.count} or more people in view for ${minutes(
            rule.thresholdMinutes
          )}`
        : `${rule.count} or more people in view`;
    case 'min_occupancy':
      return `Fewer than ${people(rule.count)} in view for ${minutes(
        rule.thresholdMinutes
      )}`;
    case 'max_visitors':
      return `More than ${people(rule.count)} pass through view in ${minutes(
        rule.windowMinutes
      )}`;
    case 'line_crossing':
      return describeCrossing(rule);
  }
}

/**
 * Why the rule cannot be saved, or null. Mirrors the API's checks. With
 * `zoneKinds` (zone id to kind) it also checks the rule addresses the
 * kind of zone it needs: a crossing rule a line, everything else an area.
 */
export function validateRule(
  rule: PresenceRule,
  zoneKinds?: Record<string, PresenceZoneKind>
): string | null {
  if (!rule.zoneId && !rule.zoneName)
    return isLineRule(rule.type) ? 'Choose a line.' : 'Choose a zone.';
  if (zoneKinds && rule.zoneId && rule.zoneId in zoneKinds) {
    const kind = zoneKinds[rule.zoneId];
    if (isLineRule(rule.type) && kind !== 'line')
      return 'This rule needs a line; draw one in the zones editor.';
    if (!isLineRule(rule.type) && kind === 'line')
      return 'This rule needs a zone, not a line.';
  }
  if (isLineRule(rule.type)) {
    if (!rule.zoneId) return 'Choose a line.';
    if (!DIRECTION_OPTIONS.some((d) => d.id === rule.direction))
      return 'Choose a direction.';
    if (!rule.methods.length)
      return 'Choose at least one way a crossing may be established.';
  }
  if (usesCount(rule.type) && (!Number.isInteger(rule.count) || rule.count < 1))
    return 'Count must be a whole number of at least 1.';
  if (usesMinutes(rule.type)) {
    if (!Number.isFinite(rule.thresholdMinutes) || rule.thresholdMinutes < 0)
      return 'Minutes must be 0 or more.';
    if (rule.type !== 'max_occupancy' && rule.thresholdMinutes === 0)
      return 'Minutes must be more than 0 for this rule.';
  }
  if (usesWindow(rule) && !isValidWindow(rule.windowMinutes))
    return `Window must be between 1 and ${MAX_RULE_WINDOW_MINUTES} minutes.`;
  return null;
}

/**
 * The caution under the minutes field for rules that read silence as
 * evidence: below the camera's own quiet period, an empty zone cannot be
 * told from a still one.
 */
export function livenessHint(
  liveness: PresenceLiveness | undefined,
  rule: PresenceRule
): string | null {
  if (!isLivenessGated(rule.type)) return null;
  const gap = liveness?.p95GapMinutes;
  if (gap == null) {
    return 'This camera only uploads when something moves. Below its quiet period, an empty zone cannot be told from a still one.';
  }
  const rounded = Math.ceil(gap);
  const base = `This camera goes quiet for up to ${rounded} min between clips. Below that, an empty zone can't be told from a still one.`;
  return rule.thresholdMinutes > 0 && rule.thresholdMinutes <= rounded
    ? `${base} Consider more than ${rounded} minutes.`
    : base;
}

export function countHint(
  rule: PresenceRule,
  peakToday: number | null | undefined
): string | null {
  if (rule.type !== 'max_occupancy') return null;
  const seen = peakToday != null ? ` Peak seen today: ${peakToday}.` : '';
  return `Counts are a floor — people hidden behind others are not counted. Set the limit against what this camera has actually seen.${seen}`;
}
